import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { RiFireFill, RiCheckLine } from 'react-icons/ri';
import { buildWeekStreak } from '../../utils/streak';
import KitsuMark from '../brand/KitsuMark';

/* ══════════════════════════════════════════════════════════════
   STREAK CELEBRATION

   Shown once, the moment a study session earns the day — before the
   session's own score screen, so the streak lands as its own beat.

   The choreography is staged rather than simultaneous. Everything
   arriving at once reads as a page that simply appeared; arriving in
   order reads as something happening *to you*:

     0ms     two marks tumble in from off-screen, rotating
     420ms   they land, squash, and settle overlapping
     520ms   sparks fire outward from the impact
     600ms   the number counts up from zero
     900ms+  label, week strip and button fade in, staggered

   The detail that makes the reference feel alive is that the shapes
   never fully stop: after landing they keep breathing and
   counter-rotating by a degree or two, forever. A composition that
   freezes on the final frame looks like a screenshot.
══════════════════════════════════════════════════════════════ */

/* Long enough for the count to feel like it is climbing, short enough
   that nobody waits on it. Capped so a 300-day streak is not a
   five-second animation. */
const COUNT_MS = 900;

function useCountUp(target, delay) {
  const [value, setValue] = useState(0);
  const raf = useRef(0);

  useEffect(() => {
    if (!target) { setValue(0); return undefined; }
    let start = 0;
    let done = false;

    const timer = setTimeout(() => {
      const step = now => {
        if (done) return;
        if (!start) start = now;
        const t = Math.min(1, (now - start) / COUNT_MS);
        /* Ease-out: fast at first, easing into the final number, so the
           last digit settles rather than snapping. */
        const eased = 1 - Math.pow(1 - t, 3);
        setValue(Math.round(target * eased));
        if (t < 1) raf.current = requestAnimationFrame(step);
      };
      raf.current = requestAnimationFrame(step);
    }, delay);

    return () => {
      done = true;
      clearTimeout(timer);
      cancelAnimationFrame(raf.current);
    };
  }, [target, delay]);

  /* Someone who asked for reduced motion gets the number, not the climb. */
  const reduce = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  return reduce ? target : value;
}

export default function StreakCelebration({ streak, lastStudied, onContinue }) {
  const cells = buildWeekStreak(streak, lastStudied);
  const shown = useCountUp(streak, 600);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = e => { if (e.key === 'Escape' || e.key === 'Enter') onContinue?.(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return createPortal(
    <div className="streak-cel" role="dialog" aria-modal="true" aria-label={`${streak} day streak`}>
      <div className="streak-cel-body">

        {/* Two shapes, thrown in from opposite sides so they meet in the
            middle. The flame sits behind and slightly left, the mark in
            front and right — overlapping, never centred on each other. */}
        <div className="streak-cel-stage" aria-hidden="true">
          <span className="streak-cel-spark-ring" />
          {[...Array(8)].map((_, i) => (
            <i key={i} className="streak-cel-spark" style={{ '--a': `${i * 45}deg` }} />
          ))}

          <span className="streak-cel-blob flame">
            <span className="streak-cel-blob-inner">
              <RiFireFill size={52} />
            </span>
          </span>

          <span className="streak-cel-blob mark">
            <span className="streak-cel-blob-inner">
              {/* The open eye, not the happy squint. A closed happy eye
                  reads as ^_^ only when there are two of them — as a
                  single central arc it just looks like a frown, which is
                  the wrong note entirely for this screen. */}
              <KitsuMark size={96} interactive={false} glow={false} />
            </span>
          </span>
        </div>

        <div className="streak-cel-count">{shown}</div>
        <div className="streak-cel-label">day streak</div>

        <div className="streak-cel-week">
          <div className="streak-cel-week-row">
            {cells.map((cell, i) => (
              <div
                key={i}
                className={`streak-cel-day${cell.active ? ' active' : ''}${cell.isToday ? ' today' : ''}`}
                style={{ animationDelay: `${1000 + i * 55}ms` }}
              >
                <span className="streak-cel-day-label">{cell.label}</span>
                <span className="streak-cel-day-dot">
                  {cell.active && <RiCheckLine size={16} />}
                </span>
              </div>
            ))}
          </div>
          <div className="streak-cel-week-note">
            Practice each day so your streak won&apos;t reset!
          </div>
        </div>
      </div>

      <div className="streak-cel-footer">
        <button type="button" className="btn-primary streak-cel-continue" onClick={onContinue} autoFocus>
          Continue
        </button>
      </div>
    </div>,
    document.body
  );
}
